import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';

const CMSProducts = () => {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 20 });
  const navigate = useNavigate();

  useEffect(() => {
    loadBrands();
    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [page, filterCategory, filterBrand, filterStatus]);

  const loadBrands = async () => {
    try {
      const res = await api.getEcommerceBrands();
      if (res.success) setBrands(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await api.getEcommerceCategories();
      if (res.success) setCategories(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (filterCategory) params.category_id = filterCategory;
      if (filterBrand) params.brand_id = filterBrand;
      if (filterStatus) params.status = filterStatus;
      const res = await api.getEcommerceProducts(params);
      if (res.success) {
        setProducts(res.data || []);
        setPagination(res.pagination || { page: 1, pages: 1, total: 0, limit: 20 });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadProducts();
  };

  const handleArchive = async (id) => {
    if (!window.confirm('Archive this product? It will be soft-deleted.')) return;
    try {
      await api.archiveEcommerceProduct(id);
      loadProducts();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this product? This cannot be undone.')) return;
    try {
      await api.deleteEcommerceProduct(id);
      loadProducts();
    } catch (e) {
      setError(e.message);
    }
  };

  const apiBase = process.env.REACT_APP_API_URL || '';
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${apiBase.replace(/\/api\/?$/, '')}${url}`;
  };

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-12">
          <Link to="/cms/products/create" className="btn btn-primary btn-sm">
            <i className="fas fa-plus"></i> New Product
          </Link>
        </div>
      </div>

      <div className="card card-outline card-primary mb-3">
        <div className="card-header">
          <h3 className="card-title">Filters</h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleSearch} className="form-inline flex-wrap gap-2">
            <input
              type="text"
              className="form-control form-control-sm mr-2 mb-1"
              placeholder="Search name or SKU"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="form-control form-control-sm mr-2 mb-1"
              value={filterCategory}
              onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.parent_name ? `${c.parent_name} › ` : ''}{c.name}</option>
              ))}
            </select>
            <select
              className="form-control form-control-sm mr-2 mb-1"
              value={filterBrand}
              onChange={(e) => { setFilterBrand(e.target.value); setPage(1); }}
            >
              <option value="">All brands</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <select
              className="form-control form-control-sm mr-2 mb-1"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
            <button type="submit" className="btn btn-sm btn-primary mb-1">
              <i className="fas fa-search"></i> Search
            </button>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Products</h3>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center p-4">Loading...</div>
          ) : (
            <table className="table table-bordered table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Image</th>
                  <th>Name</th>
                  <th>SKU</th>
                  <th>Category</th>
                  <th>Brand</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th style={{ width: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.length > 0 ? (
                  products.map((p) => {
                    const imgUrl = p.primary_image_url ? getImageUrl(p.primary_image_url) : null;
                    return (
                      <tr key={p.id}>
                        <td className="align-middle text-center">
                          {imgUrl ? (
                            <img src={imgUrl} alt="" style={{ maxWidth: 50, maxHeight: 50, objectFit: 'cover' }} />
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="align-middle">
                          <strong>{p.name}</strong>
                          {p.is_featured && <span className="badge badge-info ml-1">Featured</span>}
                        </td>
                        <td className="align-middle"><code>{p.sku}</code></td>
                        <td className="align-middle">{p.category_name || '—'}</td>
                        <td className="align-middle">{p.brand_name || '—'}</td>
                        <td className="align-middle">{Number(p.price).toFixed(2)}</td>
                        <td className="align-middle">{p.stock_quantity}</td>
                        <td className="align-middle">
                          <span className={`badge ${
                            p.status === 'published' ? 'bg-success' :
                            p.status === 'draft' ? 'bg-warning' : 'bg-secondary'
                          }`}>
                            {p.status}
                          </span>
                        </td>
                        <td className="align-middle">{p.updated_at ? new Date(p.updated_at).toLocaleDateString() : '—'}</td>
                        <td className="align-middle">
                          <Link to={`/cms/products/edit/${p.id}`} className="btn btn-sm btn-info mr-1">
                            <i className="fas fa-edit"></i>
                          </Link>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary mr-1"
                            onClick={() => handleArchive(p.id)}
                            title="Archive"
                          >
                            <i className="fas fa-archive"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(p.id)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center">No products found</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {pagination.pages > 1 && (
          <div className="card-footer clearfix">
            <span className="text-muted">
              Page {pagination.page} of {pagination.pages} ({pagination.total} total)
            </span>
            <ul className="pagination pagination-sm m-0 float-right">
              <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                <button type="button" className="page-link" onClick={() => setPage((p) => Math.max(1, p - 1))}>«</button>
              </li>
              <li className={`page-item ${page >= pagination.pages ? 'disabled' : ''}`}>
                <button type="button" className="page-link" onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}>»</button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default CMSProducts;
